import type { DirectiveBinding, ObjectDirective } from 'vue';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { checkButtonPermission, ensurePermissionsLoaded, checkSpecialFeature } from '@/shared/utils/permissionUtils';
import { useDialog } from '@/shared/composables/useDialog';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';

/**
 * v-permission-button
 *
 * 버튼에 data-menu-key, data-button-name 속성을 부여하고
 * 세션의 permissions.menus 기반으로 버튼 클릭 권한을 체크한다.
 *
 * 사용 예:
 * <v-btn
 *   v-permission-button
 *   :data-menu-key="'/entity_my'"
 *   :data-button-name="'Add Entity'"
 *   @click="goToCreate"
 * >
 *   +Add Entity
 * </v-btn>
 */
const permissionButtonDirective: ObjectDirective<HTMLElement, void> = {
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    const sessionStore = useSessionStore();
    const { alert } = useDialog();
    const languageStore = useLanguageStore();
    const { createSyncT } = useTranslation();
    const t = createSyncT(languageStore.currentLanguage);

    // Special feature mapping (whitelist) - limited scope to avoid side effects
    // Key format: `${menuKey}:${buttonName}`
    const SPECIAL_MAP: Record<string, string> = {
      '/marketplace:Subscribe': 'Marketplace Subscribe',
      '/marketplace:Cancel': 'Marketplace Subscribe', // grouped with Subscribe
    };

    const onClick = async (event: MouseEvent) => {
      try {
        // data-attribute 또는 바인딩 값에서 메뉴 키 / 버튼 이름 추출
        const menuKey =
          el.getAttribute('data-menu-key') || (binding.value as any)?.menuKey;
        const buttonName =
          el.getAttribute('data-button-name') || (binding.value as any)?.buttonName;

        // 메뉴 키나 버튼 이름이 없으면 기존 버튼과 동일하게 동작 (레거시 호환)
        if (!menuKey || !buttonName) {
          return;
        }

        // 세션 permissions 가 없으면 한 번만 백엔드에서 재로드
        const permissions = await ensurePermissionsLoaded();

        // Authorization 기능이 비활성화된 경우(비-MY 등) 그대로 통과
        if (!permissions || permissions.isAuthEnabled === false) {
          // eslint-disable-next-line no-console
          console.log('[v-permission-button] auth disabled or no permissions', {
            menuKey,
            buttonName,
            isAuthEnabled: permissions?.isAuthEnabled,
          });
          return;
        }

        const allowed = checkButtonPermission(permissions, menuKey, buttonName);

        // ✅ 버튼별 최종 체크 결과 로그
        // eslint-disable-next-line no-console
        console.log('[v-permission-button] check result', {
          menuKey,
          buttonName,
          allowed,
          menuPerm: permissions.menus?.[menuKey]
            ? {
                view: permissions.menus[menuKey].view,
                button: (permissions.menus[menuKey] as any)[buttonName] || null,
              }
            : null,
        });

        if (!allowed) {
          // If action is not allowed, check if this button is whitelisted to use SpecialFeature
          const mapKey = `${menuKey}:${buttonName}`;
          const specialKey = SPECIAL_MAP[mapKey];
          if (specialKey && permissions) {
            const specialAllowed = checkSpecialFeature(permissions as any, specialKey);
            if (specialAllowed) {
              // eslint-disable-next-line no-console
              console.log('[v-permission-button] allowed by SpecialFeature', {
                menuKey,
                buttonName,
                specialKey,
              });
              return; // treat as allowed → let the original @click proceed
            }
          }
          // 패시브 모드: 클릭을 막지 않고 그대로 통과
          // 서버에서 403(AUTH002) 시 전역 에러 핸들러가 access.PermissionDenied로 표시한다.
          // eslint-disable-next-line no-console
          console.warn('[v-permission-button] not allowed (pass-through mode)', {
            menuKey,
            buttonName,
          });
          return;
        }
        // allowed === true 이면 아무 것도 하지 않고 @click 으로 넘김
      } catch (error) {
        // 권한 체크 중 예외가 나더라도 "무반응" 상태로 끝나지 않도록 방어
        console.error('[v-permission-button] error while checking permission:', error);
        // 에러 시에는 권한 체크를 건너뛰고 기본 동작(@click)을 그대로 수행
        return;
      }
    };

    // 버블 단계에서 실행하여 Vuetify 내부 핸들러와 충돌을 줄임
    el.addEventListener('click', onClick, false);
  },
};

export default permissionButtonDirective;

